import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoMdClose } from "react-icons/io";

/**
 * Modal do back-office.
 *
 * Resolve o que o modal do flowbite não entregava bem aqui: renderiza em
 * portal (nada de conflito de z-index com drawer/tabela), prende o foco,
 * fecha no ESC, trava o scroll da página e mantém cabeçalho e rodapé fixos
 * enquanto só o corpo rola. Em telas pequenas vira bottom sheet.
 */

const SIZES = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-3xl",
  xl: "sm:max-w-5xl",
  "2xl": "sm:max-w-6xl",
  full: "sm:max-w-[95vw]",
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  headerAccessory,
  toolbar,
  footer,
  children,
  size = "lg",
  closeOnBackdrop = true,
  bodyClassName = "",
}) => {
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // `handleClose` precisa ser ESTÁVEL. O efeito de foco abaixo depende dele, e
  // quem usa o Modal costuma passar um `onClose` inline (função nova a cada
  // render). Com o estado do formulário no mesmo componente, cada tecla
  // renderizava de novo, refazia o efeito e, 60 ms depois, o foco voltava ao
  // primeiro campo: digitar o 2º campo pulava para o 1º. O `onClose` mais
  // recente fica numa ref, então o Esc e o clique no fundo ainda chamam o atual.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const handleClose = useCallback(() => {
    onCloseRef.current?.(false);
  }, []);

  // Monta primeiro e só então liga a transição, senão a animação de entrada
  // não roda (o elemento nasceria já no estado final).
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      const frame = requestAnimationFrame(() => setIsVisible(true));

      return () => cancelAnimationFrame(frame);
    }

    setIsVisible(false);
    const timeout = setTimeout(() => setIsMounted(false), 200);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!isMounted) return undefined;

    previousFocusRef.current = document.activeElement;

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const focusTimeout = setTimeout(() => {
      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
      const target = focusable?.[0];

      // O botão de fechar é o primeiro do painel; pular para o campo seguinte
      // evita abrir o modal com o "X" destacado.
      (focusable?.[1] || target)?.focus?.();
    }, 60);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleClose();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        panelRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) || []
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;
      clearTimeout(focusTimeout);
      previousFocusRef.current?.focus?.();
    };
  }, [isMounted, handleClose]);

  if (!isMounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
    >
      <div
        className={`absolute inset-0 bg-slate-900/50 backdrop-blur-[2px] transition-opacity duration-200 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={closeOnBackdrop ? handleClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        className={`relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl transition-all duration-200 sm:max-h-[90vh] sm:rounded-2xl ${
          SIZES[size] || SIZES.lg
        } ${
          isVisible
            ? "translate-y-0 opacity-100 sm:scale-100"
            : "translate-y-6 opacity-0 sm:translate-y-0 sm:scale-95"
        }`}
      >
        {/* Alça de arraste visual do bottom sheet no mobile. */}
        <div className="flex justify-center pt-2 sm:hidden">
          <span className="h-1 w-10 rounded-full bg-slate-200" />
        </div>

        <header className="flex shrink-0 items-start gap-3 border-b border-slate-100 px-5 py-4">
          {Icon ? (
            <span className="mt-0.5 rounded-xl bg-pink-50 p-2 text-pink-600">
              <Icon size={18} />
            </span>
          ) : null}

          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-slate-900">
              {title}
            </h2>
            {subtitle ? (
              <p className="truncate text-xs text-slate-500">{subtitle}</p>
            ) : null}
          </div>

          {headerAccessory ? (
            <div className="flex shrink-0 items-center gap-2">
              {headerAccessory}
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <IoMdClose size={18} />
          </button>
        </header>

        {toolbar ? (
          <div className="shrink-0 border-b border-slate-100 bg-slate-50/60 px-5 py-3">
            {toolbar}
          </div>
        ) : null}

        <div
          className={`modal-scroll flex-1 overflow-y-auto overscroll-contain px-5 py-4 ${bodyClassName}`}
        >
          {children}
        </div>

        {footer ? (
          <footer className="shrink-0 border-t border-slate-100 bg-white px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
